import { PublicHeader, PublicFooter } from "@/components/layouts/PublicLayout";
import AuthenticatedPageLayout from "@/components/layouts/AuthenticatedPageLayout";
import { useAuth } from "@/context/AuthContext";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const faqs = [
  {
    question: "What is the Free A.I. Business Evaluation?",
    answer: "It's a personalized report generated after you complete a brief questionnaire about your business. We analyze your industry, assess your situation, and deliver actionable insights—all at no cost and with no commitment required."
  },
  {
    question: "Who is this service for?",
    answer: "This service is designed for established small and medium-sized businesses that want expert guidance on A.I. adoption. If you're a business owner or executive trying to understand what A.I. means for your industry, this is for you."
  },
  {
    question: "Do you implement A.I. solutions?",
    answer: "No. We focus exclusively on advisory services. We help you understand what A.I. can and should do for your business, but we don't sell or implement A.I. tools. This keeps our guidance objective and focused on your best interests."
  },
  {
    question: "How do you determine what service is right for me?",
    answer: "After your free evaluation, we review your business context—company size, operational complexity, and specific needs—to recommend an engagement that fits your situation. Our goal is to provide clarity that prevents costly missteps, not to upsell services you don't need."
  },
  {
    question: "What happens after I complete the questionnaire?",
    answer: "We generate your personalized A.I. Business Evaluation report. You can review it at your own pace. If it makes sense to continue, we'll discuss service options appropriate for your situation—no pressure, no obligation."
  },
  {
    question: "Is there a satisfaction guarantee?",
    answer: "Yes. If you engage our services and are not satisfied with our assessment and recommendations, we'll work with you to address any concerns. Your confidence in our guidance is paramount."
  },
  {
    question: "Why do you focus on context before discussing services?",
    answer: "Because meaningful guidance requires understanding your specific situation first. Generic advice leads to generic outcomes. We invest in understanding your business before making any recommendations—that's how we ensure the guidance you receive is actually relevant."
  },
  {
    question: "What industries do you serve?",
    answer: "We work with businesses across multiple industries including Manufacturing, Retail, Healthcare, Professional Services, Financial Services, Technology, Construction, Transportation, and Hospitality."
  }
];

const FAQPage = () => {
  const { user } = useAuth();

  const content = (
    <section className="py-24 bg-gradient-to-b from-navy-950 to-navy-900">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="text-primary font-medium mb-4 tracking-wide uppercase text-sm">
            Frequently Asked Questions
          </p>
          <h1 className="font-heading text-5xl md:text-6xl font-extrabold text-white mb-6 tracking-tight">
            Got Questions?
          </h1>
          <p className="text-lg text-slate-300">
            Find answers to common questions about our A.I. advisory services.
          </p>
        </div>

        <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
          {faqs.map((faq, index) => (
            <AccordionItem
              key={index}
              value={`item-${index}`}
              className="glass rounded-xl px-6 border-none"
            >
              <AccordionTrigger className="text-white font-heading font-semibold text-left hover:text-primary py-6">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-slate-300 pb-6">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* Still have questions */}
        <div className="mt-16 text-center glass rounded-2xl p-8">
          <h3 className="font-heading text-xl font-semibold text-white mb-4">
            Still have questions?
          </h3>
          <p className="text-slate-300 mb-6">
            We're here to help. Reach out and we'll get back to you promptly.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={user ? "/portal/contact" : "/contact"} data-testid="faq-contact-link">
              <Button variant="outline" className="border-slate-700 text-slate-300 hover:text-white rounded-full px-8 py-4">
                Contact Us
              </Button>
            </Link>
            <Link to="/get-started" data-testid="faq-cta-link">
              <Button className="bg-primary hover:bg-primary/90 text-white rounded-full px-8 py-4">
                Get Your Free Evaluation
              </Button>
            </Link>
          </div>
          <p className="text-slate-500 text-sm mt-6">
            Or <Link to="/about-founder" className="text-primary hover:underline">meet our founder</Link> to learn more about who's behind A.I. Means Business.
          </p>
        </div>
      </div>
    </section>
  );

  // Render with authenticated layout if user is logged in
  if (user) {
    return <AuthenticatedPageLayout>{content}</AuthenticatedPageLayout>;
  }

  // Render with public layout for anonymous users
  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      {content}
      <PublicFooter />
    </div>
  );
};

export default FAQPage;
